/**
 * The harness wire vocabulary.
 *
 * Planning traces to `docs/ai-builder/m1-plan-review-contract.md`; the execution and context
 * additions trace to `docs/ai-builder/execution-sync.md` at harness c51994b3. Read the source
 * contract rather than assuming older handoff documents describe the deployed backend.
 *
 * The looseness below is deliberate: an interrupt's `payload` is whatever the model passed as tool
 * arguments, so anything narrower than `unknown` would assert a shape the wire does not
 * guarantee. The guards at the foot of the file are the other half of that decision — payloads
 * are coerced (`payload.util.ts`, `brief-display.util.ts`), the interrupts carrying them are
 * checked. Each is `toX(value: unknown): X | null` so the caller decides what a rejection means:
 * the stream drops a bad frame and keeps going, a read endpoint fails the call.
 */
import {
  HarnessApproval,
  HarnessConversationDetails,
  HarnessPlanAdjustment,
  HarnessTargetAdvanced,
  HarnessTurnContext,
  HarnessWriteTarget,
  toApproval,
  toConversationDetails,
  toTurnContext,
  toWriteTarget,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import {
  asArray,
  asNumber,
  asString,
  asStringList,
  isRecord,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'
import {
  planUsesStories,
  toStoryPlanSnapshot,
} from '@flowx/designer/modules/ai-builder/utils/story-plan/story-plan-contract.util'

/** Valid entries survive malformed neighbours, but an invalid collection is never a total. */
export type StoryCollection<T> = {
  availability: 'available' | 'missing' | 'invalid'
  entries: readonly T[]
}

export type HarnessStoryState =
  'not_started' | 'implementing' | 'implemented' | 'needs_you' | 'stopped'

export type HarnessStoryResource = { label: string | null; name: string | null }
export type HarnessStoryDependency = { story_id: string; names: StoryCollection<string> }
export type HarnessStoryQuestion = {
  question: string | null
  options: StoryCollection<string>
  recommended: string | null
  story_id: string | null
}

export type HarnessStory = {
  story_id: string
  /** Original one-based position, including any unrenderable rows in the supplied list. */
  position: number
  name: string | null
  outcome: string | null
  state: HarnessStoryState | null
  forecast: boolean | null
  commit_id: string | null
  reason: string | null
  question: HarnessStoryQuestion | null
  questionAvailability: StoryCollection<never>['availability']
  resources: Record<'created' | 'edited' | 'removed', StoryCollection<HarnessStoryResource>>
  dependencies: StoryCollection<HarnessStoryDependency>
  decided_while_building: StoryCollection<string>
  serves: StoryCollection<string>
  operations: StoryCollection<string>
  step_count: number | null
}

export type HarnessStoryDecision = {
  story_id: string | null
  text: string | null
  chosen: string | null
  alternatives: StoryCollection<string>
}

export type HarnessGoalContract = {
  goal: string | null
  milestones: StoryCollection<{
    milestone_id: string
    acceptance: StoryCollection<string>
  }>
  acceptance: StoryCollection<{ acceptance_id: string; statement: string | null }>
}

/** One source/version only. Callers choose review OR record OR run, never merge their fields. */
export type HarnessStoryPlanSnapshot = {
  expansion: string | null
  goal: string | null
  stories: StoryCollection<HarnessStory>
  contract: HarnessGoalContract | null
  contractAvailability: StoryCollection<never>['availability']
  decisions: StoryCollection<HarnessStoryDecision>
}

/** R2 parses these replayable frames; identity includes the run, not just the story. */
export type HarnessStoryFrame = {
  type: 'story_started' | 'story_implemented'
  eventId: string
  runId: string
  storyId: string
  name: string
  commitId: string | null
}

/**
 * Switch on this, never on the event name — the name carries strictly less. `fix_review` and
 * `ship_review` both arrive as `interrupt`, and — deliberately — **`brief_review` and
 * `plan_review` both arrive as `plan_proposed`**, so that a plan surface receives the brief
 * without a contract change. A client keying on the event name cannot tell a brief from a plan.
 */
export type HarnessInterruptType =
  | 'ask_user'
  | 'brief_review'
  | 'plan_review'
  | 'direct_review'
  | 'fix_review'
  | 'ship_review'
  | 'specialist_review'
  /** The conversation asks to read the project the maker's page is in. */
  | 'read_grant'

/** The interrupts whose document is the plan: one the agent proposed, and a direct edit it wants signed
 * off before it writes. Both put the same document on screen and take the same review row, so the
 * surfaces ask this rather than each listing the pair — a sixth type is then one edit here. */
const PLAN_REVIEW_TYPES: readonly HarnessInterruptType[] = ['plan_review', 'direct_review']

/** Every interrupt that asks the maker to read a document and answer on it, rather than answer a
 * question or approve a step. */
const DOCUMENT_REVIEW_TYPES: readonly HarnessInterruptType[] = [
  'brief_review',
  ...PLAN_REVIEW_TYPES,
]

/** Takes the store's `interruptType()` as it comes — null when no interrupt is open. */
export function isPlanReviewType(interruptType: HarnessInterruptType | null | undefined): boolean {
  return !!interruptType && PLAN_REVIEW_TYPES.includes(interruptType)
}

export function isDocumentReviewType(
  interruptType: HarnessInterruptType | null | undefined
): boolean {
  return !!interruptType && DOCUMENT_REVIEW_TYPES.includes(interruptType)
}

export type HarnessInterrupt = {
  interrupt_id: string
  type: HarnessInterruptType
  payload: unknown
  resume_schema: unknown
}

/** `stream_truncated` has no wire counterpart — the transport synthesizes it when a stream ends
 * without an `end` frame. The contract calls that a crash, so it must become a visible error
 * rather than a stream that quietly completes. */
export type HarnessFrame =
  | HarnessStoryFrame
  | { type: 'token'; text: string }
  // `label` is an optional human phrase for the activity row — "Reading the project-data-type
  // …" rather than `read_resource`. Additive on the wire, so `name` remains the fallback.
  | { type: 'tool_use_start'; toolUseId: string; name: string; label: string; arguments: unknown }
  | { type: 'tool_use_result'; toolUseId: string; name: string; label: string; content: unknown }
  // In the harness's emitted vocabulary but not in the Notion spec — tolerated rather than
  // treated as unknown, so an additive wire does not read as a broken one.
  | { type: 'finding_result'; content: unknown }
  | { type: 'interrupt'; interrupt: HarnessInterrupt }
  /**
   * Planning stopped without producing anything a maker may approve, and says why. Deliberately
   * not an `error`: nothing failed technically, the *result* did not meet the bar — so no interrupt
   * frame precedes it and none should be shown. `recoveryAction` is written for the maker.
   */
  | {
      type: 'planning_failed'
      code: string
      reason: string
      hardFailures: readonly string[]
      recoveryAction: string
      planId: string
    }
  /** The session's durable record, sent once just before `end` on an indexed session. */
  | { type: 'lifecycle'; record: HarnessSessionRecord }
  /** A verified, committed run: the branch it landed on, in this same conversation.
   * `target_advanced` says where its write target moved. */
  | { type: 'branch_ready'; handoff: HarnessBranchHandoff }
  /** The harness's own commit moved the conversation's write target on. */
  | { type: 'target_advanced'; advanced: HarnessTargetAdvanced }
  /** The plan adjusted itself at a checkpoint of its run, or asks the maker to. */
  | { type: 'plan_adjusted'; adjustment: HarnessPlanAdjustment }
  | { type: 'error'; message: string; retryable: boolean; code: string }
  /**
   * The turn stopped before it finished — a disconnect, a restart, a hang, a lost lease — and a new
   * turn is the whole remedy. Not an `error` (nothing was refused) and not `blocked` (nothing is
   * owed but a retry). Live it is followed by `end {paused: false}`; stored it is the turn's last
   * event, with no `end` after it. A cancelled resume does not restore its gate.
   */
  | { type: 'interrupted'; code: string; message: string }
  /** What the turn was charged — a character-based estimate, not the provider's count — and what is
   * left of today's budget. Sent once, after the turn's work and before `end`. */
  | { type: 'usage'; estimatedTokens: number; remainingToday: number }
  | { type: 'end'; paused: boolean }

/** What the front-end can send to `/resume`. `plan_review`'s schema advertises an `edits` field
 * the endpoint still ignores (harness TD-5), so *plan* iteration stays prose-only. */
export type HarnessDecision = 'approve' | 'reject' | 'respond'

/**
 * Structured steering on a brief, which the endpoint does accept. `decision_id` is derived
 * harness-side from the decision's own words rather than its position, so it survives reordering
 * and revision.
 *
 * Resume offers one channel for a `respond`, so the harness renders these into an explicit
 * imperative instruction to the model rather than passing them as typed data. It genuinely acts
 * on them — but the phrasing is its own, so a flip reads as a decision, not a suggestion.
 */
export type HarnessDecisionChange = {
  decision_id: string
  choose: string
  reason?: string
}

/** **Matched by the question's own text, not by an id** — the harness's choice, and the right
 * one: `OpenQuestion` has no id, the text is what `steerable_handles.open_questions` lists, and a
 * minted id would have to survive a revision rewording the question. */
export type HarnessQuestionAnswer = {
  question: string
  answer: string
}

export type HarnessSteering = {
  change_decisions?: HarnessDecisionChange[]
  add_out_of_scope?: string[]
  /**
   * **Dead by construction until the harness fixed it.** The prompt forbids briefing with a
   * blocking question, so every question a brief may contain is non-blocking — and the handle
   * exposed only blockers, which made a real fork reach the maker as decoration. It now lists
   * every unresolved question, and this is what answers one.
   */
  answer_questions?: HarnessQuestionAnswer[]
}

/**
 * The read endpoints' bodies, as the front end consumes them — **what is READ, not everything the
 * wire carries.** `POST /sessions` also returns `thread_id` and `GET /sessions/active` a `status`;
 * nothing branches on either, so the guards below do not require them. A guard stricter than its
 * consumers turns an additive wire change into an outage.
 */
export type HarnessSessionHandle = {
  session_id: string
}

/**
 * `GET /sessions/{id}` — what the harness is waiting on and, for a session it indexed, the whole
 * durable record behind it.
 *
 * Two shapes share the endpoint: a session opened before the lifecycle index answers
 * `{paused, interrupt}` and nothing else, one created since carries its `record` too. `paused`
 * and `interrupt` are read from both so recovery has one thing to branch on.
 *
 * A snapshot, not a state, to keep it apart from the store's own `HarnessSessionState`.
 */
export type HarnessSessionSnapshot = {
  paused: boolean
  interrupt: HarnessInterrupt | null
  record: HarnessSessionRecord | null
  /** The maker's own feedback on this session, keyed by message id. Deliberately here and not on
   * `record`: a `lifecycle` frame is the record minus its events, and parking it there would
   * blank it at the end of every turn. */
  feedback: Readonly<Record<string, HarnessMessageFeedback>>
  /** The conversation's own fields, on a read made as its owner. Absent on a read scoped to a
   * version. */
  conversation?: HarnessConversationDetails | null
}

/** A maker's verdict on one agent message. `Up` is the whole answer; `Down` carries the kinds of
 * wrong behind it. The members are the harness's own vocabulary and travel as they are. */
export enum HarnessFeedbackVerdict {
  Up = 'up',
  Down = 'down',
}

/**
 * One message's recorded feedback, as `GET /sessions/{id}` returns it under `feedback`.
 *
 * The harness files far more per row than this — the prompt, the whole agent reply of that turn,
 * its tool trace — but it reads all of that off the session's own events at write time. None of it
 * comes back, and nothing here needs it.
 */
export type HarnessMessageFeedback = {
  messageId: string
  verdict: HarnessFeedbackVerdict
  /** UI-owned codes, not labels — the harness stores whatever it is sent. Codes this build does
   * not recognise are kept rather than dropped: feedback left from a newer designer must not
   * vanish in an older one, and the maker is the same person either way. */
  reasons: readonly string[]
  comment: string | null
}

/**
 * Where the maker was standing when they rated a message — the half of a verdict the session
 * cannot know. The harness reads the prompt, the reply and the tool trace off its own events.
 *
 * **Nothing identifying the maker belongs in it.** The harness keys a row by the username it takes
 * from `Fx-OriginalUserInfo` and keeps nothing else about them, on purpose.
 *
 * Lives here rather than beside the service that fills it: the store needs the type, and importing
 * it from the service closes `feedback-context → ai-builder.store → harness-session.store` into a
 * cycle that leaves the service unconstructable (NG0200).
 */
export type FeedbackClientContext = {
  schema_version: 1
  /** `AiBuilderView`, repeated rather than imported to keep this file a leaf. */
  tab: 'make' | 'plan'
  designer_build: string
  environment: string | null
  org_code: string | null
  workspace_id: string | null
  workspace_name: string | null
  workspace_type: string | null
  /** The project and version are named by id only — their names live in a route-scoped store a
   * root service cannot reach, and the ids resolve them anyway. */
  app_id: string | null
  app_version_id: string | null
  branch: string
  is_org_admin: boolean
}

/** What the UI sends to record or change feedback. `client_context` is ours and bounded harness-side
 * at 16 000 bytes; the scope fields repeat on every harness body, as everywhere else here. */
export type HarnessFeedbackRequest = {
  verdict: HarnessFeedbackVerdict
  reasons: readonly string[]
  comment: string | null
  clientContext: Readonly<Record<string, unknown>>
}

function isFeedbackVerdict(value: unknown): value is HarnessFeedbackVerdict {
  return Object.values(HarnessFeedbackVerdict).some((verdict) => verdict === value)
}

export function toMessageFeedback(value: unknown): HarnessMessageFeedback | null {
  if (!isRecord(value)) {
    return null
  }

  const messageId = asString(value['message_id'])
  const verdict = value['verdict']

  if (!messageId || !isFeedbackVerdict(verdict)) {
    return null
  }

  return {
    messageId,
    verdict,
    reasons: asStringList(value['reasons']),
    comment: asString(value['comment']) || null,
  }
}

/**
 * The `feedback` map a session record carries, dropping any entry this client cannot read.
 *
 * Absent is an empty map rather than a failure: the route ships behind the designer, so a harness
 * without it answers a session that simply carries no feedback — which is also true of a session
 * nobody has rated.
 */
export function toMessageFeedbackMap(
  value: unknown
): Readonly<Record<string, HarnessMessageFeedback>> {
  if (!isRecord(value)) {
    return {}
  }

  // Keyed by the id the entry itself carries rather than by the key it arrived under, so a map
  // that disagrees with its own rows cannot file feedback against the wrong message.
  return Object.fromEntries(
    Object.values(value)
      .map(toMessageFeedback)
      .filter(Boolean)
      .map((feedback) => [feedback.messageId, feedback])
  )
}

/** One public frame the harness kept of a session, in the order it happened. `user` is the
 * maker's message (or their decision, as `Decision: approve`); every other name is an SSE event
 * name with its data, tokens coalesced and tool frames reduced to their outcome. */
export type HarnessSessionEvent = {
  event: string
  data: Record<string, unknown>
}

/** `running` is a turn in flight under a lease — reported as `interrupted` once the lease lapses
 * without a terminal frame. `waiting` is an interrupt outstanding. `blocked` is a `planning_failed` or
 * `error` frame on the last turn: nothing approvable remains. */
export type HarnessSessionStatus =
  'idle' | 'running' | 'waiting' | 'finished' | 'blocked' | 'interrupted'

/** Preparation and execution state persisted by the harness, including recovered runs. */
export type HarnessPlanState =
  'DRAFT' | 'READY' | 'EXECUTING' | 'PAUSED' | 'COMPLETED' | 'ABANDONED'

/** How far the harness got preparing the plan: `drafting`, `generating_items`, `ready`,
 * `revising_items`. Read as words, never branched on — a new phase must not break the rail. */
export type HarnessPlanPreparation = string

export type HarnessExecutionStatus =
  'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'interrupted'

/** The statuses a run is still the harness's rather than the maker's. */
const IN_FLIGHT_EXECUTION_STATUSES: readonly HarnessExecutionStatus[] = ['queued', 'running']

/** The statuses a run can be picked up from: the branch is there either way, and the harness
 * reconciles what it finds on it before writing again. */
const RESUMABLE_EXECUTION_STATUSES: readonly HarnessExecutionStatus[] = [
  'paused',
  'failed',
  'interrupted',
]

export function isExecutionInFlight(status: HarnessExecutionStatus | undefined): boolean {
  return !!status && IN_FLIGHT_EXECUTION_STATUSES.includes(status)
}

/** A run's error as the maker reads it: the harness's maker text, else the raw error an older
 * harness sends alone. */
export function makerErrorOf(run: HarnessExecutionRun | null | undefined): string {
  return run?.maker_error || run?.error || ''
}

export function isExecutionResumable(status: HarnessExecutionStatus | undefined): boolean {
  return !!status && RESUMABLE_EXECUTION_STATUSES.includes(status)
}

/** What one run may carry, and so what a plan must fit in to be runnable at all. The harness
 * enforces it; saying it once here keeps the guard, the blocker's wording and both defaults that
 * send it on the wire from drifting apart. */
export const MAX_WORK_ITEMS = 100

/** The harness caps the saved-changes commit message. Stated once so the field cannot accept what
 * the guard then silently drops. */
export const COMMIT_MESSAGE_MAX_LENGTH = 255

/** One work item as the record keeps it — only what the execution controls need. `execution` is
 * the typed intent the model wrote for the step (`operation_id` plus its fields). A step can also
 * be untyped: it names only its `operation_id`, and the harness decides its typed intent when the
 * run reaches it. Either way the step has an operation, which is what makes it runnable
 * (`stepOperationId`). The rows in full are read from the interrupt payload instead. */
export type HarnessWorkItemRecord = {
  work_item_id: string
  description?: string
  depends_on: readonly string[]
  target_from_item?: string
  execution: (Record<string, unknown> & { operation_id: string }) | null
  /** An untyped step's operation. Only read when the step has no `execution`. */
  operation_id?: string
}

/** The operation a step runs: its typed intent's, or an untyped step's own. The harness reads a
 * step the same way (`step_operation_id`), so a step the designer offers to run is one it admits. */
export function stepOperationId(item: HarnessWorkItemRecord): string | null {
  return item.execution ? item.execution.operation_id : (item.operation_id ?? null)
}

export type HarnessExecutionTarget =
  | { mode: 'new_branch'; commit_message?: string }
  | { mode: 'current_branch'; branch_id: string; commit_message?: string }

export type HarnessCommitReview = {
  required: boolean
  review_id: string | null
  commit_message: string | null
  changes: readonly Record<string, unknown>[]
}

/** A change to what the app's runtime serves (`application.policy.serve`). It moves every runtime
 * user at once, so the run is refused (409) unless `review_id` comes back as
 * `confirmed_commit_review_id` — the same field a commit review's id travels in. On a plan that
 * also needs a commit review the harness makes the two ids one, so one confirmation covers both. */
export type HarnessPolicyReview = {
  review_id: string
  before: string
  served_now: string
  after: string
  blast_radius: string
}

/** The review fields of a prepared run — the same allowlist the harness publishes. */
export type HarnessItemPreview = {
  /** Never set: item previews carry no discriminator on the wire, and `toItemPreview` refuses any
   * `type`. Declared only so `type === 'plan'` narrows the union, which `undefined` or `never`
   * cannot do without `strictNullChecks`. */
  type?: 'item'
  execution_target?: HarnessExecutionTarget & { excludes_saved_uncommitted_changes?: boolean }
  commit_review?: HarnessCommitReview
  policy_review?: HarnessPolicyReview
  /** The message the item will commit, stamped by the harness with the intent, the plan and the
   * approver. Read-only: it is what outlives the conversation, so the maker sees it, never edits it. */
  commit_message?: string
  /** On an amendment preview: why the plan changes, and the revised description it asks the maker
   * to confirm again when the intent itself moved (L3). */
  amendment_reason?: string
  reconfirm_description?: Record<string, unknown>
  preview_id: string
  item_id: string
  plan_version: number
  /** Absent on the wire for the original pilot operation, which is the rename. */
  operation_id: string
  summary?: string
  details: readonly unknown[]
  display_name?: string
  old_name?: string
  new_name?: string
  process_name?: string
}

export type HarnessPlanPreview = Omit<HarnessItemPreview, 'type' | 'item_id' | 'operation_id'> & {
  type: 'plan'
  /** Absent only on item plans; present even when the story projection is unavailable. */
  storyPlan?: HarnessStoryPlanSnapshot
  items: readonly HarnessExecutionItem[]
  total_items: number
}

export type HarnessExecutionItem = {
  item_id: string
  operation_id: string
  summary: string
  status: string
  /** As on an item preview; present on a plan preview's items. */
  commit_message?: string
  depends_on: readonly string[]
  target_from_item?: string
  run: HarnessExecutionRun | null
}

export type HarnessExecutionPreview = HarnessItemPreview | HarnessPlanPreview

export type HarnessExecutionRun = HarnessExecutionPreview & {
  completed_items?: number
  active_item_id?: string | null
  head_commit_id?: string | null
  pause_requested?: boolean
  error_code?: string | null
  requires_new_preview?: boolean
  preliminary_commit_id?: string | null
  run_id: string
  status: HarnessExecutionStatus
  phase: string
  error: string | null
  /** The stopped item's error in the maker's words, beside `error`, the raw refusal. */
  maker_error?: string | null
  commit_id: string | null
  branch: { wip_version_id: string; branch_name: string; branch_id: string } | null
  completed_at?: string
  /** The stopped item could not be rendered because the render host is unavailable, and the maker
   * may continue it unverified — explicitly, recorded and stamped on its commit. */
  render_unverified_offer?: { item_id: string; reason: string }
  /** The last reassessment at a checkpoint: what the harness decided and, when blocked, what would
   * unblock it. */
  adjustment?: { level: string; reason: string; unblock: string }
  /** Why the run stopped and asks, in the maker's words (`stop.maker`, with the resources it
   * names); the raw refusal, or a reassessment's reason, stays `error`. */
  stop?: { code: string; maker: HarnessMakerStop | null }
  deferred_edits?: readonly HarnessDeferredEdit[]
  /** Whether the app launches, read once the run completed: `launchable` is null when it could not
   * be read, `served` whether the runtime serves this run's version, and `message` the harness's
   * own words for a maker. */
  launch?: { launchable: boolean | null; served: boolean; message: string }
}

/** A resource a stop names, for a "Take me there": its kind is the harness's resource type. */
export type HarnessStopRef = {
  kind: string
  resource_id: string
  name: string | null
  app_version_id: string | null
}

/** A stop said for the maker: the headline, the rest, what they can do, and what to open. */
export type HarnessMakerStop = {
  headline: string
  detail: string
  fix: string
  refs: readonly HarnessStopRef[]
}

function toMakerStop(value: unknown): HarnessMakerStop | null {
  if (!isRecord(value) || !(asString(value['headline']) || asString(value['detail']))) {
    return null
  }
  return {
    headline: asString(value['headline']),
    detail: asString(value['detail']),
    fix: asString(value['fix']),
    refs: asArray(value['refs']).flatMap((ref) =>
      isRecord(ref) && asString(ref['kind']) && asString(ref['resource_id'])
        ? [
            {
              kind: asString(ref['kind']),
              resource_id: asString(ref['resource_id']),
              name: asString(ref['name']) || null,
              app_version_id: asString(ref['app_version_id']) || null,
            },
          ]
        : []
    ),
  }
}

export type HarnessDeferredEdit = { at: string; deferred_reason?: string }

/** A change a turn asked for while the plan ran, queued for the next item boundary. Its items are
 * the proposal's; the amendment preview is what turns them into a reviewable diff. */
export type HarnessQueuedAmendment = {
  from_plan_version: number
  proposed_items: readonly Record<string, unknown>[]
  reason: string
}

export type HarnessPlanRecord = {
  /** Read this normalized snapshot directly; do not reparse the record or merge it with a review. */
  storyPlan?: HarnessStoryPlanSnapshot
  plan_id: string
  state: HarnessPlanState
  version: number
  description_revision: number
  preparation: HarnessPlanPreparation
  baseline_commit_id: string | null
  execution_branch_id: string | null
  work_items: readonly HarnessWorkItemRecord[]
  /** Every operation the plan declares. An outline-only plan has no work items yet, so this is the
   * only place its operations are named, and the gateway admits a run only on this set. */
  operation_ids?: readonly string[]
  /** The same steps with every field the harness sent, for the plan display. On a story run they
   * are its projection: the run's steps, then the approved steps of the stories not opened yet. */
  work_item_payloads?: readonly Record<string, unknown>[]
  execution: HarnessExecutionRun | null
  /** Set once the maker confirmed the description; null while it is still a draft. */
  confirmed: boolean
  /** A change queued for the next item boundary; absent while none is. */
  amendment?: HarnessQueuedAmendment | null
  /** Who approved the current version, and how. */
  approval?: HarnessApproval | null
  /** Whether the agent may adjust this plan within what was approved (D15). Default on. */
  allow_adjustments?: boolean
}

/** Where a completed run committed: the result version and branch, in the same conversation.
 * `source_*` identifies the request, so a handoff for another conversation can be refused. */
export type HarnessBranchHandoff = {
  app_id: string
  app_version_id: string
  branch: string
  session_id: string
  source_session_id: string
  source_app_version_id: string
  source_branch: string
  run_id: string
}

/** The conversation's durable record, read by its owner or sent in a lifecycle frame. `durable`
 * is false on a harness running a memory lifecycle store, where records are lost on restart. */
export type HarnessSessionRecord = {
  session_id: string
  title: string
  status: HarnessSessionStatus
  revision: number
  durable: boolean
  paused: boolean
  updated_at: string
  events: readonly HarnessSessionEvent[]
  interrupt: HarnessInterrupt | null
  plan: HarnessPlanRecord | null
  /** Set on a continuation session: what was implemented and where it came from. */
  handoff: (HarnessBranchHandoff & { summary: string }) | null
  /** Where this conversation writes; absent until a change is confirmed. The record carries it,
   * so a `lifecycle` frame keeps it current. */
  write_target?: HarnessWriteTarget | null
  /** Where the maker last spoke from; absent until a turn sends it. */
  last_context?: HarnessTurnContext | null
}

/** Error codes worth distinguishing in the UI. Anything else renders generically. */
export const HARNESS_ERROR_CODES = {
  /** The maker's daily token cap. Arrives on a normal HTTP 200 stream, so it must read as a
   * refusal, not as a failed request. */
  budgetExceeded: 'budget_exceeded',
  /** The harness reached an interrupt with no checkpointer configured. This is a misconfigured
   * service, not a failure of the maker's request, and the copy must say so. */
  noCheckpointer: 'no_checkpointer',
  /** The session has no interrupt to answer any more — the service restarted with a memory
   * checkpointer, or the decision was already taken elsewhere. Nothing was applied. */
  noPendingInterrupt: 'no_pending_gate',
  /** Synthesized locally: the stream ended without its mandatory `end` frame. */
  streamTruncated: 'stream_truncated',
  /** Synthesized locally from a 401. ai-gateway rejected the call before the harness saw it,
   * so the designer session is invalid — a retry cannot help, only logging in again. */
  notAuthenticated: 'not_authenticated',
  /** Synthesized locally from any other non-2xx: the harness (or the relay) answered, and its
   * answer is the message. Distinct from a cut stream, which has no answer to show. */
  httpError: 'http_error',
  /** Synthesized locally: a 2xx whose body is not the shape the endpoint promises. Its own code
   * because `paused: undefined` read as "not paused" quietly forgets a session. */
  malformedResponse: 'malformed_response',
} as const

/** The interrupt types as values for runtime membership checks. */
const INTERRUPT_TYPES: readonly HarnessInterruptType[] = [
  'ask_user',
  'brief_review',
  'plan_review',
  'direct_review',
  'fix_review',
  'ship_review',
  'specialist_review',
  'read_grant',
]

const SESSION_STATUSES: readonly HarnessSessionStatus[] = [
  'idle',
  'running',
  'waiting',
  'finished',
  'blocked',
  'interrupted',
]

const PLAN_STATES: readonly HarnessPlanState[] = [
  'DRAFT',
  'READY',
  'EXECUTING',
  'PAUSED',
  'COMPLETED',
  'ABANDONED',
]

const EXECUTION_STATUSES: readonly HarnessExecutionStatus[] = [
  'queued',
  'running',
  'paused',
  'completed',
  'failed',
  'interrupted',
]

/**
 * An interrupt, checked rather than asserted: every surface in the module switches on `type`,
 * so an unknown type would render a view for an interrupt that does not exist, and one with no
 * `interrupt_id` cannot be answered at all. `payload` and `resume_schema` stay `unknown` — the
 * guard checks the interrupt, never the cargo, which has no schema to check against.
 */
export function toInterrupt(value: unknown): HarnessInterrupt | null {
  if (!isRecord(value)) {
    return null
  }

  const interruptId = value['interrupt_id']
  const interruptType = value['type']

  return typeof interruptId === 'string' &&
    !!interruptId &&
    INTERRUPT_TYPES.includes(interruptType as HarnessInterruptType)
    ? {
        interrupt_id: interruptId,
        type: interruptType as HarnessInterruptType,
        payload: value['payload'],
        resume_schema: value['resume_schema'],
      }
    : null
}

/** `POST /sessions`. Only `session_id` is read; `thread_id` is on the wire and consumed by
 * nothing, so requiring it would reject a session that works. */
export function toSessionHandle(value: unknown): HarnessSessionHandle | null {
  if (!isRecord(value)) {
    return null
  }

  const sessionId = value['session_id']

  return typeof sessionId === 'string' && !!sessionId ? { session_id: sessionId } : null
}

/**
 * `paused` must be a real boolean: it is what the whole recovery path branches on, and an absent
 * one read as `false` is how an outstanding interrupt becomes a forgotten session. A `null` interrupt
 * is legitimate — a session that finished — but a malformed one fails the snapshot rather than
 * being flattened to null. The record rides along when the body is one; `revision` is the tell.
 */
export function toSessionSnapshot(value: unknown): HarnessSessionSnapshot | null {
  if (!isRecord(value) || typeof value['paused'] !== 'boolean') {
    return null
  }

  const raw = value['interrupt']
  const interrupt = raw == null ? null : toInterrupt(raw)

  if (raw != null && !interrupt) {
    return null
  }

  const record = typeof value['revision'] === 'number' ? toSessionRecord(value) : null

  const conversation = toConversationDetails(value)

  return {
    paused: value['paused'],
    interrupt,
    record,
    feedback: toMessageFeedbackMap(value['feedback']),
    // The conversation's own fields, when the owner read includes them.
    ...(conversation && { conversation }),
  }
}

/**
 * A session's durable record, from an owner read, a lifecycle frame or an abandon's answer. The
 * three fields every consumer branches on are required — `session_id`, `status`, `revision` —
 * and the rest default to their empty form. A malformed interrupt drops the record: an interrupt
 * that cannot be answered must not be shown as one that can.
 */
export function toSessionRecord(value: unknown): HarnessSessionRecord | null {
  if (!isRecord(value)) {
    return null
  }

  const sessionId = value['session_id']
  const status = value['status']
  const revision = value['revision']

  if (
    typeof sessionId !== 'string' ||
    !sessionId ||
    !SESSION_STATUSES.includes(status as HarnessSessionStatus) ||
    typeof revision !== 'number'
  ) {
    return null
  }

  const raw = value['interrupt']
  const interrupt = raw == null ? null : toInterrupt(raw)

  if (raw != null && !interrupt) {
    return null
  }

  const handoff = toBranchHandoff(value['handoff'])
  const writeTarget = toWriteTarget(value['write_target'])
  const lastContext = toTurnContext(value['last_context'])

  return {
    session_id: sessionId,
    title: asString(value['title']),
    status: status as HarnessSessionStatus,
    revision,
    // Absent is read as durable: only an explicit false is the memory-mode warning.
    durable: value['durable'] !== false,
    paused: value['paused'] === true || !!interrupt,
    updated_at: asString(value['updated_at']),
    events: asArray(value['events']).flatMap((entry) =>
      isRecord(entry) && typeof entry['event'] === 'string' && isRecord(entry['data'])
        ? [{ event: entry['event'], data: entry['data'] }]
        : []
    ),
    interrupt,
    plan: toPlanRecord(value['plan']),
    handoff:
      handoff && isRecord(value['handoff'])
        ? { ...handoff, summary: asString(value['handoff']['summary']) }
        : null,
    // Each only once there is one: no target before a change is confirmed, no context before a
    // turn sends one.
    ...(writeTarget && { write_target: writeTarget }),
    ...(lastContext && { last_context: lastContext }),
  }
}

function toPlanRecord(value: unknown): HarnessPlanRecord | null {
  if (!isRecord(value)) {
    return null
  }

  const planId = value['plan_id']
  const state = value['state']

  if (typeof planId !== 'string' || !PLAN_STATES.includes(state as HarnessPlanState)) {
    return null
  }

  const amendment = toQueuedAmendment(value['amendment'])
  const approval = currentApproval(value)

  return {
    ...(planUsesStories(value) && { storyPlan: toStoryPlanSnapshot(value) }),
    plan_id: planId,
    state: state as HarnessPlanState,
    version: asNumber(value['version'], 0),
    description_revision: asNumber(value['description_revision'], 0),
    preparation: asString(value['preparation']),
    baseline_commit_id: asString(value['baseline_commit_id']) || null,
    execution_branch_id: asString(value['execution_branch_id']) || null,
    operation_ids: asArray(value['operation_ids']).filter(
      (id): id is string => typeof id === 'string' && !!id
    ),
    work_items: asArray(value['work_items']).flatMap((item) => {
      if (!isRecord(item) || typeof item['work_item_id'] !== 'string') {
        return []
      }

      const execution = item['execution']

      return [
        {
          work_item_id: item['work_item_id'],
          description: asString(item['description']) || undefined,
          depends_on: asArray(item['depends_on']).map((id) => asString(id)),
          ...(typeof item['target_from_item'] === 'string' && {
            target_from_item: item['target_from_item'],
          }),
          execution:
            isRecord(execution) && typeof execution['operation_id'] === 'string'
              ? { ...execution, operation_id: execution['operation_id'] }
              : null,
          // An untyped step: an operation and no typed intent yet. A typed intent without an
          // operation is not untyped, and the harness does not run it either.
          ...(execution == null &&
            typeof item['operation_id'] === 'string' &&
            item['operation_id'] && { operation_id: item['operation_id'] }),
        },
      ]
    }),
    work_item_payloads: asArray(value['work_items']).filter(
      (item): item is Record<string, unknown> =>
        isRecord(item) && typeof item['work_item_id'] === 'string'
    ),
    execution: toExecutionRun(value['execution']),
    confirmed: value['confirmed_description'] != null,
    // Each only once the plan has it: an approval once confirmed, an amendment while one is queued.
    ...(amendment && { amendment }),
    ...(approval && { approval }),
    ...(isRecord(value['approval_options']) && {
      allow_adjustments: value['approval_options']['allow_adjustments'] !== false,
    }),
  }
}

function toQueuedAmendment(value: unknown): HarnessQueuedAmendment | null {
  if (!isRecord(value)) {
    return null
  }

  return {
    from_plan_version: asNumber(value['from_plan_version'], 0),
    proposed_items: asArray(value['proposed_items']).filter(isRecord),
    reason: asString(value['reason']),
  }
}

/** The approval of the version the plan is at, falling back to the one it was confirmed with
 * before its first version exists — the harness's own reading for its plan summaries. */
function currentApproval(plan: Record<string, unknown>): HarnessApproval | null {
  const current = asArray(plan['versions']).find(
    (version) => isRecord(version) && version['version'] === plan['version']
  )

  return (
    (isRecord(current) ? toApproval(current['approval']) : null) ?? toApproval(plan['approval'])
  )
}

/** The execution pilot's original operation, which the harness leaves implicit on the wire. */
export const DEFAULT_OPERATION_ID = 'process.node.rename'

/** `preview_id`, `item_id` and `plan_version` are what the run request echoes back, so a preview
 * without them cannot be run and is refused here. */
export function toItemPreview(value: unknown): HarnessItemPreview | null {
  // Only a plan preview is discriminated, so any `type` means this is not an item preview.
  if (!isRecord(value) || value['type'] !== undefined) {
    return null
  }

  const previewId = value['preview_id']
  const itemId = value['item_id']
  const planVersion = value['plan_version']

  if (
    typeof previewId !== 'string' ||
    !previewId ||
    typeof itemId !== 'string' ||
    !itemId ||
    typeof planVersion !== 'number'
  ) {
    return null
  }

  return {
    ...executionReview(value),
    preview_id: previewId,
    item_id: itemId,
    plan_version: planVersion,
    operation_id: asString(value['operation_id']) || DEFAULT_OPERATION_ID,
    summary: asString(value['summary']) || undefined,
    details: asArray(value['details']),
    display_name: asString(value['display_name']) || undefined,
    old_name: asString(value['old_name']) || undefined,
    new_name: asString(value['new_name']) || undefined,
    process_name: asString(value['process_name']) || undefined,
  }
}

export function toExecutionPreview(value: unknown): HarnessExecutionPreview | null {
  if (!isRecord(value) || value['type'] !== 'plan') {
    return toItemPreview(value)
  }

  if (
    typeof value['preview_id'] !== 'string' ||
    !value['preview_id'] ||
    typeof value['plan_version'] !== 'number' ||
    !Array.isArray(value['items'])
  ) {
    return null
  }
  const items: HarnessExecutionItem[] = []
  for (const item of value['items']) {
    if (
      !isRecord(item) ||
      typeof item['item_id'] !== 'string' ||
      !item['item_id'] ||
      typeof item['operation_id'] !== 'string' ||
      !item['operation_id']
    ) {
      return null
    }
    items.push({
      item_id: item['item_id'],
      operation_id: item['operation_id'],
      summary: asString(item['summary']),
      status: asString(item['status']),
      ...(typeof item['commit_message'] === 'string' &&
        !!item['commit_message'] && { commit_message: item['commit_message'] }),
      depends_on: asArray(item['depends_on']).map((id) => asString(id)),
      ...(typeof item['target_from_item'] === 'string' && {
        target_from_item: item['target_from_item'],
      }),
      // Nested plan execution is not part of this protocol.
      run:
        isRecord(item['run']) && item['run']['type'] !== 'plan'
          ? toExecutionRun(item['run'])
          : null,
    })
  }
  return {
    ...executionReview(value),
    ...(planUsesStories(value) && { storyPlan: toStoryPlanSnapshot(value) }),
    type: 'plan',
    preview_id: value['preview_id'],
    plan_version: value['plan_version'],
    summary: asString(value['summary']),
    display_name: asString(value['display_name']),
    details: asArray(value['details']),
    items,
    total_items: asNumber(value['total_items'], items.length),
  }
}

function executionReview(
  value: Record<string, unknown>
): Pick<
  HarnessItemPreview,
  | 'execution_target'
  | 'commit_review'
  | 'policy_review'
  | 'commit_message'
  | 'amendment_reason'
  | 'reconfirm_description'
> {
  const target = value['execution_target']
  const review = value['commit_review']
  const policy = value['policy_review']
  const reconfirm = value['reconfirm_description']
  return {
    ...(typeof value['commit_message'] === 'string' &&
      !!value['commit_message'] && { commit_message: value['commit_message'] }),
    ...(typeof value['amendment_reason'] === 'string' &&
      !!value['amendment_reason'] && { amendment_reason: value['amendment_reason'] }),
    ...(isRecord(reconfirm) && { reconfirm_description: reconfirm }),
    ...(isRecord(target) &&
      (target['mode'] === 'new_branch' ||
        (target['mode'] === 'current_branch' && typeof target['branch_id'] === 'string')) && {
        execution_target:
          target['mode'] === 'new_branch'
            ? {
                mode: 'new_branch' as const,
                ...(typeof target['excludes_saved_uncommitted_changes'] === 'boolean' && {
                  excludes_saved_uncommitted_changes: target['excludes_saved_uncommitted_changes'],
                }),
              }
            : { mode: 'current_branch' as const, branch_id: target['branch_id'] as string },
      }),
    ...(isRecord(review) && {
      commit_review: {
        // An incomplete review must fail closed rather than become automatic consent.
        required: review['required'] !== false,
        review_id: asString(review['review_id']) || null,
        commit_message: asString(review['commit_message']) || null,
        changes: asArray(review['changes']).filter(isRecord),
      },
    }),
    // Kept even without an id: a review the run cannot confirm must still block the run button
    // rather than vanish and let the maker walk into the 409.
    ...(isRecord(policy) && {
      policy_review: {
        review_id: asString(policy['review_id']),
        before: asString(policy['before']),
        served_now: asString(policy['served_now']),
        after: asString(policy['after']),
        blast_radius: asString(policy['blast_radius']),
      },
    }),
  }
}

export function toExecutionRun(value: unknown): HarnessExecutionRun | null {
  const preview = toExecutionPreview(value)

  if (!preview || !isRecord(value)) {
    return null
  }

  const runId = value['run_id']
  const status = value['status']

  if (
    typeof runId !== 'string' ||
    !runId ||
    !EXECUTION_STATUSES.includes(status as HarnessExecutionStatus)
  ) {
    return null
  }

  const branch = value['branch']
  const offer = value['render_unverified_offer']
  const adjustment = value['adjustment']
  const stop = value['stop']
  const deferred = Array.isArray(value['deferred_edits'])
    ? value['deferred_edits'].filter(
        (edit): edit is Record<string, unknown> => isRecord(edit) && typeof edit['at'] === 'string'
      )
    : []
  const launch = value['launch']

  return {
    ...preview,
    ...(isRecord(launch) && {
      launch: {
        launchable: typeof launch['launchable'] === 'boolean' ? launch['launchable'] : null,
        served: isRecord(launch['this_version']) && launch['this_version']['served'] === true,
        message: asString(launch['message']),
      },
    }),
    ...(deferred.length > 0 && {
      deferred_edits: deferred.map((edit) => ({
        at: asString(edit['at']),
        ...(typeof edit['deferred_reason'] === 'string' && {
          deferred_reason: edit['deferred_reason'],
        }),
      })),
    }),
    ...(isRecord(offer) &&
      typeof offer['item_id'] === 'string' && {
        render_unverified_offer: { item_id: offer['item_id'], reason: asString(offer['reason']) },
      }),
    ...(isRecord(adjustment) &&
      typeof adjustment['level'] === 'string' && {
        adjustment: {
          level: adjustment['level'],
          reason: asString(adjustment['reason']),
          unblock: asString(adjustment['unblock']),
        },
      }),
    ...(isRecord(stop) && {
      stop: { code: asString(stop['code']), maker: toMakerStop(stop['maker']) },
    }),
    ...(value['completed_items'] != null && {
      completed_items: asNumber(value['completed_items'], 0),
    }),
    ...(value['active_item_id'] != null && { active_item_id: asString(value['active_item_id']) }),
    ...(value['head_commit_id'] != null && { head_commit_id: asString(value['head_commit_id']) }),
    ...(value['pause_requested'] != null && { pause_requested: value['pause_requested'] === true }),
    ...(value['error_code'] != null && { error_code: asString(value['error_code']) }),
    ...(value['requires_new_preview'] != null && {
      requires_new_preview: value['requires_new_preview'] === true,
    }),
    ...(value['preliminary_commit_id'] != null && {
      preliminary_commit_id: asString(value['preliminary_commit_id']),
    }),
    run_id: runId,
    status: status as HarnessExecutionStatus,
    phase: asString(value['phase']),
    error: asString(value['error']) || null,
    ...(value['maker_error'] != null && { maker_error: asString(value['maker_error']) || null }),
    commit_id: asString(value['commit_id']) || null,
    branch:
      isRecord(branch) && typeof branch['wip_version_id'] === 'string'
        ? {
            wip_version_id: branch['wip_version_id'],
            branch_name: asString(branch['branch_name']),
            branch_id: asString(branch['branch_id']),
          }
        : null,
    completed_at: asString(value['completed_at']) || undefined,
  }
}

const HANDOFF_FIELDS = [
  'app_id',
  'app_version_id',
  'branch',
  'session_id',
  'source_session_id',
  'source_app_version_id',
  'source_branch',
  'run_id',
] as const

/** Every field is required: a handoff missing its destination cannot be followed, and one missing
 * its source cannot be checked against the session that asked for it. */
export function toBranchHandoff(value: unknown): HarnessBranchHandoff | null {
  if (!isRecord(value)) {
    return null
  }

  const handoff: Partial<Record<(typeof HANDOFF_FIELDS)[number], string>> = {}

  for (const field of HANDOFF_FIELDS) {
    const entry = value[field]

    if (typeof entry !== 'string' || !entry) {
      return null
    }

    handoff[field] = entry
  }

  return handoff as HarnessBranchHandoff
}
